/**
 * @fileoverview The landing page's "Get Started" role picker.
 *
 * Hans now sells to three audiences, and they don't get in through the same
 * door: an instructor signs themselves up, a student redeems an invite, and an
 * organization is set up with us. Three equal buttons in the hero could not
 * carry that — a button reading "Continue as Organization" promises a signup
 * that doesn't exist — so the hero keeps ONE primary action and the choice
 * happens here, where each card has a line to explain itself.
 *
 * Reads the roles from lib/authPortals so the cards can't drift from the
 * sign-in tabs.
 */

import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AUTH_PORTALS, offeredPortals } from "@/lib/authPortals";

const EASE = [0.16, 1, 0.3, 1];

/**
 * @param {{open: boolean, onOpenChange: (open: boolean) => void}} props
 */
const RoleChooserDialog = ({ open, onOpenChange }) => {
  // `offeredPortals()` with no active portal drops anything switched off — the
  // student card follows STUDENT_PORTAL_ENABLED exactly like the student tab.
  const roles = offeredPortals();
  const firstRoleRef = useRef(null);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-theme="dark"
        data-testid="role-chooser"
        /* Land on the first choice rather than Radix's default (the close
           button), which otherwise takes the focus ring and reads as the
           dialog's primary action. Focus still enters the dialog, so the trap
           and screen-reader announcement are unaffected. */
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          firstRoleRef.current?.focus();
        }}
        className="max-w-lg border-[var(--studio-divider-strong)] bg-[var(--studio-base)] p-6 sm:p-7"
      >
        <DialogHeader className="text-center sm:text-center">
          <DialogTitle
            className="text-xl font-light text-[var(--text-primary)]"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            How will you use Hans?
          </DialogTitle>
          <DialogDescription className="text-sm text-[var(--text-secondary)]">
            Pick the one that fits — you'll land in the right place.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 space-y-2">
          {roles.map((role, i) => {
            const RoleIcon = role.icon;
            return (
              <motion.div
                key={role.id}
                /* Kept short and shallow on purpose: the dialog is a decision
                   point, so a long stagger leaves it looking empty at exactly
                   the moment someone is reading it. */
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04, duration: 0.22, ease: EASE }}
              >
                <Link
                  ref={i === 0 ? firstRoleRef : undefined}
                  to={role.chooserTo}
                  onClick={() => onOpenChange(false)}
                  data-testid={`role-chooser-${role.id}`}
                  className="group flex items-center gap-4 rounded-2xl border border-[var(--studio-divider)]
                             bg-[var(--studio-raised)] p-4 outline-hidden transition-colors
                             hover:border-[var(--studio-divider-strong)] hover:bg-[var(--studio-overlay)]
                             focus-visible:border-[var(--accent-yellow)]"
                >
                  <div
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl
                               bg-[var(--studio-interactive)] text-[var(--text-secondary)]
                               transition-colors group-hover:text-[var(--accent-yellow-text)]"
                  >
                    <RoleIcon className="h-5 w-5" strokeWidth={1.5} aria-hidden />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p
                      className="text-sm font-medium text-[var(--text-primary)]"
                      style={{ fontFamily: "Outfit, sans-serif" }}
                    >
                      {role.chooserTitle}
                    </p>
                    <p className="mt-0.5 text-xs leading-snug text-[var(--text-tertiary)]">
                      {role.chooserDesc}
                    </p>
                  </div>
                  <ArrowRight
                    className="h-4 w-4 shrink-0 text-[var(--text-quaternary)] transition-all
                               group-hover:translate-x-0.5 group-hover:text-[var(--accent-yellow-text)]"
                    strokeWidth={1.5}
                    aria-hidden
                  />
                </Link>
              </motion.div>
            );
          })}
        </div>

        <p className="mt-1 text-center text-xs text-[var(--text-tertiary)]">
          Already have an account?{" "}
          <Link
            to={AUTH_PORTALS[0].loginPath}
            onClick={() => onOpenChange(false)}
            className="text-[var(--accent-yellow-text)] hover:underline"
          >
            Sign in
          </Link>
        </p>
      </DialogContent>
    </Dialog>
  );
};

export default RoleChooserDialog;
